import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, Upload, Download, Trash2, Search, Filter,
  File, Image, Presentation, Table, X, Plus,
  CheckCircle, Lock, Users, Eye, AlertCircle, FolderOpen,
  FileSignature, ClipboardList, Palette, Scale, Receipt,
  BookOpen, CheckSquare, Calculator, ShieldCheck, Home,
  Scroll, Folder, Loader2, Grid, List, Star
} from 'lucide-react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RESOURCE_CATEGORIES = {
  contratos: { name: 'Contratos', icon: FileSignature, color: 'bg-blue-100 text-blue-700', description: 'Compraventa, arrendamiento, exclusividad' },
  formatos: { name: 'Formatos de Trabajo', icon: ClipboardList, color: 'bg-green-100 text-green-700', description: 'Captación, prospección, seguimiento' },
  templates_mkt: { name: 'Templates Marketing', icon: Palette, color: 'bg-pink-100 text-pink-700', description: 'Redes sociales, flyers, presentaciones' },
  legal: { name: 'Documentos Legales', icon: Scale, color: 'bg-purple-100 text-purple-700', description: 'Poderes, cartas, convenios' },
  fiscal: { name: 'Documentos Fiscales', icon: Receipt, color: 'bg-yellow-100 text-yellow-700', description: 'Formatos SAT, declaraciones' },
  guias: { name: 'Guías y Manuales', icon: BookOpen, color: 'bg-indigo-100 text-indigo-700', description: 'Procedimientos, tutoriales' },
  checklists: { name: 'Checklists', icon: CheckSquare, color: 'bg-teal-100 text-teal-700', description: 'Verificación de trámites' },
  presentaciones: { name: 'Presentaciones', icon: Presentation, color: 'bg-orange-100 text-orange-700', description: 'PPT captación, valuación' },
  calculadoras: { name: 'Calculadoras Excel', icon: Calculator, color: 'bg-emerald-100 text-emerald-700', description: 'Comisiones, ROI, valuación' },
  nom247: { name: 'NOM-247', icon: ShieldCheck, color: 'bg-red-100 text-red-700', description: 'Contratos PROFECO' },
  avaluos: { name: 'Avalúos', icon: Home, color: 'bg-cyan-100 text-cyan-700', description: 'Formatos de avalúo' },
  escrituras: { name: 'Escrituras', icon: Scroll, color: 'bg-amber-100 text-amber-700', description: 'Documentos notariales' },
  otros: { name: 'Otros', icon: Folder, color: 'bg-gray-100 text-gray-700', description: 'Otros recursos' }
};

const ACCESS_TIERS = {
  public: { name: 'Público', color: 'bg-gray-100 text-gray-700', description: 'Todos pueden descargar' },
  free_user: { name: 'Usuarios Registrados', color: 'bg-blue-100 text-blue-700', description: 'Solo usuarios registrados' },
  paying: { name: 'Socios de Pago', color: 'bg-green-100 text-green-700', description: 'Solo miembros de pago' },
  vip: { name: 'Solo VIP', color: 'bg-purple-100 text-purple-700', description: 'Exclusivo VIP' },
  admin: { name: 'Solo Admin', color: 'bg-red-100 text-red-700', description: 'Solo administradores' }
};

export default function AdminBiblioteca() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const bulkInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState([]);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterAccess, setFilterAccess] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  
  // Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [bulkCategory, setBulkCategory] = useState('formatos');
  const [bulkAccess, setBulkAccess] = useState('paying');
  const [uploadResults, setUploadResults] = useState([]);
  
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    resource_category: 'formatos',
    subcategory: '',
    access_tier: 'paying',
    tags: '',
    is_featured: false
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadMaterials();
  }, [navigate, token, filterCategory, filterAccess]);

  const loadMaterials = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterCategory) params.append('resource_category', filterCategory);
      if (filterAccess) params.append('access_tier', filterAccess);
      params.append('limit', '100');

      const response = await axios.get(`${API_URL}/api/content/materials?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const mats = response.data.materials || [];
      setMaterials(mats);
      
      // Calculate stats
      const catStats = {};
      Object.keys(RESOURCE_CATEGORIES).forEach(cat => {
        catStats[cat] = mats.filter(m => m.resource_category === cat).length;
      });
      setStats({
        total: mats.length,
        categories: catStats,
        downloads: mats.reduce((acc, m) => acc + (m.download_count || 0), 0)
      });
    } catch (error) {
      console.error('Error loading materials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) {
        setError('El archivo excede el límite de 100MB');
        return;
      }
      setSelectedFile(file);
      if (!uploadData.title) {
        setUploadData(prev => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, '') }));
      }
    }
  };

  const handleBulkSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => f.size <= 100 * 1024 * 1024);
    if (validFiles.length < files.length) {
      setError(`${files.length - validFiles.length} archivos exceden el límite de 100MB`);
    }
    setSelectedFiles(validFiles);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setError('Selecciona un archivo');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', uploadData.title);
      formData.append('description', uploadData.description || '');
      formData.append('resource_category', uploadData.resource_category);
      formData.append('subcategory', uploadData.subcategory || '');
      formData.append('access_tier', uploadData.access_tier);
      formData.append('tags', uploadData.tags || '');
      formData.append('is_featured', uploadData.is_featured);

      await axios.post(`${API_URL}/api/content/materials/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadData({ title: '', description: '', resource_category: 'formatos', subcategory: '', access_tier: 'paying', tags: '', is_featured: false });
      setUploadProgress(0);
      setSuccess('Material subido exitosamente');
      loadMaterials();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al subir archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Selecciona archivos para subir');
      return;
    }

    setUploading(true);
    setError('');
    setUploadResults([]);

    const results = [];
    let completed = 0;

    for (const file of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
        formData.append('description', '');
        formData.append('resource_category', bulkCategory);
        formData.append('access_tier', bulkAccess);

        await axios.post(`${API_URL}/api/content/materials/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        results.push({ name: file.name, success: true });
      } catch (error) {
        results.push({ name: file.name, success: false, error: error.response?.data?.detail || 'Error' });
      }

      completed++;
      setUploadProgress(Math.round((completed / selectedFiles.length) * 100));
      setUploadResults([...results]);
    }

    setUploading(false);
    setSuccess(`${results.filter(r => r.success).length} de ${selectedFiles.length} archivos subidos`);
    loadMaterials();
  };

  const handleDownload = async (materialId, fileName) => {
    try {
      const response = await axios.get(`${API_URL}/api/content/materials/${materialId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.open(`${API_URL}${response.data.download_url}`, '_blank');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al descargar');
    }
  };

  const handleDelete = async (materialId) => {
    if (!window.confirm('¿Eliminar este recurso?')) return;
    try {
      await axios.delete(`${API_URL}/api/content/materials/${materialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadMaterials();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const filteredMaterials = materials.filter(m =>
    m.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.file_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-biblioteca-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FolderOpen className="w-7 h-7 text-purple-500" />
              Biblioteca de Recursos Profesionales
            </h1>
            <p className="text-gray-500 text-sm">Contratos, formatos, templates y documentos para socios UPIM</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowBulkModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition-colors"
              data-testid="bulk-upload-btn"
            >
              <Plus className="w-4 h-4" />
              Subida Masiva
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
              data-testid="upload-btn"
            >
              <Upload className="w-4 h-4" />
              Subir Recurso
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={() => setError('')} className="ml-auto">×</button>
          </div>
        )}

        {/* Category Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {Object.entries(RESOURCE_CATEGORIES).slice(0, 7).map(([key, cat]) => {
            const IconComponent = cat.icon;
            const count = stats.categories?.[key] || 0;
            return (
              <button
                key={key}
                onClick={() => setFilterCategory(filterCategory === key ? '' : key)}
                className={`p-3 rounded-xl border transition-all ${
                  filterCategory === key 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-100 hover:border-purple-200 bg-white'
                }`}
              >
                <div className={`p-2 rounded-lg ${cat.color} w-fit mb-2`}>
                  <IconComponent className="w-4 h-4" />
                </div>
                <p className="text-xs font-medium text-gray-900 truncate">{cat.name}</p>
                <p className="text-lg font-bold text-gray-900">{count}</p>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar recursos..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Todas las categorías</option>
            {Object.entries(RESOURCE_CATEGORIES).map(([key, cat]) => (
              <option key={key} value={key}>{cat.name}</option>
            ))}
          </select>
          <select
            value={filterAccess}
            onChange={(e) => setFilterAccess(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Todos los accesos</option>
            {Object.entries(ACCESS_TIERS).map(([key, tier]) => (
              <option key={key} value={key}>{tier.name}</option>
            ))}
          </select>
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4 rounded-xl text-white">
            <p className="text-3xl font-bold">{stats.total || 0}</p>
            <p className="text-purple-100">Total Recursos</p>
          </div>
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 rounded-xl text-white">
            <p className="text-3xl font-bold">{stats.downloads || 0}</p>
            <p className="text-green-100">Descargas Totales</p>
          </div>
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 rounded-xl text-white">
            <p className="text-3xl font-bold">{Object.keys(stats.categories || {}).filter(k => stats.categories[k] > 0).length}</p>
            <p className="text-blue-100">Categorías con Contenido</p>
          </div>
        </div>

        {/* Materials Grid/List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            Cargando recursos...
          </div>
        ) : filteredMaterials.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
            <FolderOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-2">No hay recursos en esta categoría</p>
            <button
              onClick={() => setShowBulkModal(true)}
              className="text-purple-500 hover:text-purple-600 font-medium"
            >
              Subir recursos masivamente
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredMaterials.map(material => {
              const category = RESOURCE_CATEGORIES[material.resource_category] || RESOURCE_CATEGORIES.otros;
              const IconComponent = category.icon;
              const accessTier = ACCESS_TIERS[material.access_tier] || ACCESS_TIERS.paying;
              
              return (
                <div key={material.id} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-lg transition-all group">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2 rounded-lg ${category.color}`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    {material.is_featured && (
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    )}
                  </div>
                  
                  <h3 className="font-medium text-gray-900 line-clamp-2 mb-1" title={material.title}>
                    {material.title}
                  </h3>
                  <p className="text-xs text-gray-500 mb-2">{material.file_name}</p>
                  
                  {material.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">{material.description}</p>
                  )}
                  
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs px-2 py-0.5 rounded ${category.color}`}>
                      {category.name}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${accessTier.color}`}>
                      {material.access_tier === 'paying' ? <Lock className="w-3 h-3 inline mr-1" /> : null}
                      {accessTier.name}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-400">
                      {material.file_size_mb || '?'} MB · {material.download_count || 0} descargas
                    </span>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleDownload(material.id, material.file_name)}
                        className="p-1.5 text-purple-500 hover:bg-purple-50 rounded"
                        title="Descargar"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(material.id)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Recurso</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Categoría</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Acceso</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Tamaño</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Descargas</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredMaterials.map(material => {
                  const category = RESOURCE_CATEGORIES[material.resource_category] || RESOURCE_CATEGORIES.otros;
                  const IconComponent = category.icon;
                  const accessTier = ACCESS_TIERS[material.access_tier] || ACCESS_TIERS.paying;
                  
                  return (
                    <tr key={material.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${category.color}`}>
                            <IconComponent className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{material.title}</p>
                            <p className="text-xs text-gray-500">{material.file_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${category.color}`}>
                          {category.name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${accessTier.color}`}>
                          {accessTier.name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{material.file_size_mb || '?'} MB</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{material.download_count || 0}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDownload(material.id, material.file_name)}
                          className="p-1.5 text-purple-500 hover:bg-purple-50 rounded"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(material.id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Single Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">Subir Recurso</h3>
                  <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleUpload} className="p-6 space-y-4">
                {/* File Zone */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                    selectedFile ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.zip"
                  />
                  {selectedFile ? (
                    <div>
                      <CheckCircle className="w-10 h-10 mx-auto text-green-500 mb-2" />
                      <p className="font-medium">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500">{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                      <p className="font-medium text-gray-700">Clic para seleccionar archivo</p>
                      <p className="text-xs text-gray-500 mt-1">PDF, Word, Excel, PPT, imágenes (máx. 100MB)</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                  <input
                    type="text"
                    value={uploadData.title}
                    onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoría *</label>
                  <select
                    value={uploadData.resource_category}
                    onChange={(e) => setUploadData({ ...uploadData, resource_category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {Object.entries(RESOURCE_CATEGORIES).map(([key, cat]) => (
                      <option key={key} value={key}>{cat.name} - {cat.description}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nivel de Acceso</label>
                  <select
                    value={uploadData.access_tier}
                    onChange={(e) => setUploadData({ ...uploadData, access_tier: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {Object.entries(ACCESS_TIERS).map(([key, tier]) => (
                      <option key={key} value={key}>{tier.name} - {tier.description}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                  <textarea
                    value={uploadData.description}
                    onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows={2}
                  />
                </div>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={uploadData.is_featured}
                    onChange={(e) => setUploadData({ ...uploadData, is_featured: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Marcar como destacado</span>
                </label>

                {uploading && (
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <p className="text-sm text-gray-500 text-center">{uploadProgress}%</p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                    disabled={uploading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={!selectedFile || uploading}
                    className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-300"
                  >
                    {uploading ? 'Subiendo...' : 'Subir Recurso'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Bulk Upload Modal */}
        {showBulkModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Plus className="w-5 h-5 text-purple-500" />
                    Subida Masiva de Recursos
                  </h3>
                  <button onClick={() => { setShowBulkModal(false); setSelectedFiles([]); setUploadResults([]); }} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Category Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categoría para todos</label>
                    <select
                      value={bulkCategory}
                      onChange={(e) => setBulkCategory(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      {Object.entries(RESOURCE_CATEGORIES).map(([key, cat]) => (
                        <option key={key} value={key}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nivel de acceso</label>
                    <select
                      value={bulkAccess}
                      onChange={(e) => setBulkAccess(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      {Object.entries(ACCESS_TIERS).map(([key, tier]) => (
                        <option key={key} value={key}>{tier.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* File Zone */}
                <div
                  onClick={() => bulkInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    selectedFiles.length > 0 ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <input
                    ref={bulkInputRef}
                    type="file"
                    onChange={handleBulkSelect}
                    className="hidden"
                    multiple
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.zip"
                  />
                  <Upload className="w-12 h-12 mx-auto text-purple-400 mb-2" />
                  <p className="font-medium text-gray-700">Seleccionar múltiples archivos</p>
                  <p className="text-sm text-gray-500 mt-1">PDF, Word, Excel, PPT, imágenes (máx. 100MB cada uno)</p>
                </div>

                {/* Selected Files */}
                {selectedFiles.length > 0 && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="font-medium text-gray-900 mb-3">{selectedFiles.length} archivos seleccionados</p>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {selectedFiles.map((file, idx) => {
                        const result = uploadResults.find(r => r.name === file.name);
                        return (
                          <div key={idx} className="flex items-center justify-between text-sm bg-white p-2 rounded-lg">
                            <span className="truncate flex-1">{file.name}</span>
                            <span className="text-gray-400 ml-2">{(file.size / (1024 * 1024)).toFixed(1)} MB</span>
                            {result && (
                              result.success 
                                ? <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
                                : <AlertCircle className="w-4 h-4 text-red-500 ml-2" title={result.error} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Progress */}
                {uploading && (
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-purple-500 h-3 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <p className="text-sm text-gray-500 text-center">
                      Subiendo... {uploadProgress}%
                    </p>
                  </div>
                )}

                {/* Results Summary */}
                {uploadResults.length > 0 && !uploading && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <p className="font-medium text-green-800">
                      {uploadResults.filter(r => r.success).length} de {uploadResults.length} archivos subidos exitosamente
                    </p>
                    {uploadResults.some(r => !r.success) && (
                      <p className="text-sm text-red-600 mt-1">
                        {uploadResults.filter(r => !r.success).length} archivos fallaron
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowBulkModal(false); setSelectedFiles([]); setUploadResults([]); }}
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                    disabled={uploading}
                  >
                    {uploadResults.length > 0 ? 'Cerrar' : 'Cancelar'}
                  </button>
                  {uploadResults.length === 0 && (
                    <button
                      onClick={handleBulkUpload}
                      disabled={selectedFiles.length === 0 || uploading}
                      className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-300 flex items-center justify-center gap-2"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Subir {selectedFiles.length} archivos
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
